import type { IPublicationAdjustment } from '@/types/publicationAdjustment';
import type { TemplateMeta } from '@/types/templateMeta';
import { request, type RequestOptions } from './client';

export interface SavePublicationAdjustmentBody {
  name: string;
  channel: string;
  template: string;
  variation?: string | null;
  values: Record<string, unknown>;
}

// list saved adjustments for the current customer, optionally restricted to one channel.
export function fetchPublicationAdjustments(
  channel?: string,
  requestOptions: RequestOptions = {}
): Promise<{ data: IPublicationAdjustment[] }> {
  return request('GET', '/v1/account/publication/adjustments', channel ? { channel } : {}, undefined, requestOptions);
}

export function createPublicationAdjustment(
  body: SavePublicationAdjustmentBody
): Promise<{ data: IPublicationAdjustment }> {
  return request('POST', '/v1/account/publication/adjustments', {}, body);
}

export function updatePublicationAdjustment(
  id: number,
  body: SavePublicationAdjustmentBody
): Promise<{ data: IPublicationAdjustment }> {
  return request('PUT', `/v1/account/publication/adjustments/${id}`, {}, body);
}

export function deletePublicationAdjustment(id: number): Promise<{ data: IPublicationAdjustment }> {
  return request('DELETE', `/v1/account/publication/adjustments/${id}`);
}

// resolve the concrete channel string an adjustment scopes to, from the template's declared
// adjustment_scope and the order context. returns null when the template opts out of persistence
// or the scoped id is unavailable.
export function resolveAdjustmentChannel(
  meta: Pick<TemplateMeta, 'adjustment_scope'> | null | undefined,
  context: { companyId?: number | null; customerId?: number | null }
): string | null {
  const scope = meta?.adjustment_scope;
  if (scope === 'company' && context.companyId) {
    return `company:${context.companyId}`;
  }
  if (scope === 'customer' && context.customerId) {
    return `customer:${context.customerId}`;
  }
  return null;
}
